import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { hasStaffAccess } from "@/lib/rbac";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  Users,
  CheckCircle2,
  UserPlus,
  PauseCircle,
  CalendarDays,
  Wallet,
  Building,
  RefreshCw,
  Clock,
  LogIn,
  LogOut,
} from "lucide-react";
import { formatDisplayDate, formatLongDate, formatMonthDay } from "@/lib/dateFormat";

const PUNCH_STATUS_STYLE = {
  Present: "bg-emerald-100 text-emerald-800 border-emerald-200",
  WFH: "bg-sky-100 text-sky-800 border-sky-200",
  "Half-day": "bg-amber-100 text-amber-800 border-amber-200",
  Absent: "bg-rose-100 text-rose-800 border-rose-200",
  Leave: "bg-violet-100 text-violet-800 border-violet-200",
  Holiday: "bg-indigo-100 text-indigo-800 border-indigo-200",
  "Weekly Off": "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = hasStaffAccess(user);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    onboarding: 0,
    pending_leaves: 0,
    payslips_this_month: 0,
  });
  const [charts, setCharts] = useState(null);
  const [myLeaves, setMyLeaves] = useState([]);
  const [myBalances, setMyBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  // Phase 10 dashboard widgets
  const [todayAtt, setTodayAtt] = useState(null);
  const [birthdays, setBirthdays] = useState([]);
  const [internsEnding, setInternsEnding] = useState([]);
  const [probationEnding, setProbationEnding] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [essDash, setEssDash] = useState(null);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [approvalInbox, setApprovalInbox] = useState({ leaves: [], letters: [] });
  const [punchBusy, setPunchBusy] = useState(false);
  const [punchErr, setPunchErr] = useState("");
  const navigate = useNavigate();

  const reload = () => {
    if (isAdmin) {
      Promise.all([
        api.get("/stats/employees").catch(() => ({ data: {} })),
        api.get("/stats/charts").catch(() => ({ data: null })),
        api.get("/attendance/today-summary").catch(() => ({ data: null })),
        api.get("/employees/upcoming-birthdays", { params: { days: 30 } }).catch(() => ({ data: [] })),
        api.get("/employees/internship-ending", { params: { days: 30 } }).catch(() => ({ data: [] })),
        api.get("/employees/probation-ending", { params: { days: 30 } }).catch(() => ({ data: [] })),
        api.get("/employees/expiring-documents", { params: { days: 60 } }).catch(() => ({ data: [] })),
      ]).then(([s, c, ta, bd, ie, pe, ed]) => {
        if (s.data) setStats((prev) => ({ ...prev, ...s.data }));
        if (c.data) setCharts(c.data);
        setTodayAtt(ta.data);
        setBirthdays(bd.data || []);
        setInternsEnding(ie.data || []);
        setProbationEnding(pe.data || []);
        setExpiringDocs(ed.data || []);
      });
    } else {
      const year = new Date().getFullYear();
      const todayIso = new Date().toISOString().slice(0, 10);
      Promise.all([
        api.get("/leaves").catch(() => ({ data: [] })),
        api.get("/leave-balances").catch(() => ({ data: [] })),
        api.get("/leave-types").catch(() => ({ data: [] })),
        api.get("/me/dashboard", { params: { days: 30 } }).catch(() => ({ data: null })),
        api.get("/holidays", { params: { year } }).catch(() => ({ data: [] })),
        api.get("/holidays", { params: { year: year + 1 } }).catch(() => ({ data: [] })),
        api.get("/approvals/inbox").catch(() => ({ data: { leaves: [], letters: [] } })),
      ]).then(([a, b, c, d, h1, h2, inbox]) => {
        setMyLeaves(a.data);
        setMyBalances(b.data);
        setLeaveTypes(c.data);
        setEssDash(d.data);
        const allHolidays = [...(h1.data || []), ...(h2.data || [])]
          .filter((h) => h?.date && h.date >= todayIso)
          .sort((x, y) => String(x.date).localeCompare(String(y.date)))
          .slice(0, 12);
        setUpcomingHolidays(allHolidays);
        setApprovalInbox(inbox.data || { leaves: [], letters: [] });
      });
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [isAdmin]);

  // Memoize derived state at top-level (rules-of-hooks: never call hooks conditionally)
  const ltMap = useMemo(
    () => Object.fromEntries(leaveTypes.map((l) => [l.id, l])),
    [leaveTypes]
  );
  const visibleBalances = useMemo(
    () => myBalances.filter((b) => ltMap[b.leave_type_id]),
    [myBalances, ltMap]
  );

  if (isAdmin) {
    const cards = [
      { label: "Total Employees", value: stats.total, color: "#226DB4", Icon: Users, to: "/employees" },
      { label: "Active", value: stats.active, color: "#089949", Icon: CheckCircle2, to: "/employees" },
      { label: "Onboarding", value: stats.onboarding, color: "#f39c12", Icon: UserPlus, to: "/employees" },
      { label: "Inactive", value: stats.inactive, color: "#dd4b39", Icon: PauseCircle, to: "/employees" },
      { label: "Pending Leaves", value: stats.pending_leaves, color: "#605ca8", Icon: CalendarDays, to: "/leaves" },
      { label: "Payslips This Month", value: stats.payslips_this_month, color: "#00c0ef", Icon: Wallet, to: "/payroll" },
    ];
    return (
      <AdminLayout title="Dashboard" breadcrumbs={["Home", "Dashboard"]}>
        <div className="flex items-center justify-end mb-3">
          <button
            onClick={reload}
            data-testid="dashboard-refresh-btn"
            title="Refresh"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-200 bg-white rounded hover:bg-slate-50 text-slate-600"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {cards.map((c) => (
            <button
              key={c.label}
              onClick={() => navigate(c.to)}
              className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden flex items-stretch hover:shadow-md transition text-left"
              data-testid={`dash-card-${c.label.replace(/ /g, "-").toLowerCase()}`}
            >
              <div
                className="flex items-center justify-center text-white px-5"
                style={{ backgroundColor: c.color }}
              >
                <c.Icon className="h-6 w-6" />
              </div>
              <div className="px-4 py-3 flex-1">
                <div className="text-2xl font-bold text-slate-800 leading-none">
                  {c.value}
                </div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-medium">
                  {c.label}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Phase 10 — Executive widgets */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Widget title="Today's Attendance" testid="widget-today-attendance" tone="emerald">
            {todayAtt ? (
              <div className="grid grid-cols-2 gap-1 text-xs">
                {["Present","WFH","Leave","Absent","Holiday","Weekly Off","Not Marked"].map((k) => (
                  <div key={k} className="flex justify-between"><span className="text-slate-500">{k}</span><span className="font-semibold">{todayAtt[k] ?? 0}</span></div>
                ))}
                <div className="col-span-2 mt-1 pt-1 border-t border-emerald-100 flex justify-between text-[11px]"><span>Total eligible</span><span className="font-semibold">{todayAtt.Total}</span></div>
              </div>
            ) : <div className="text-xs text-slate-400">No data</div>}
          </Widget>

          <Widget title={`Upcoming Birthdays (${birthdays.length})`} testid="widget-birthdays" tone="pink">
            {birthdays.length === 0 ? <div className="text-xs text-slate-400">No birthdays in next 30 days</div> :
              <ul className="text-xs space-y-1 max-h-44 overflow-y-auto">
                {birthdays.map((b) => (
                  <li key={b.id} className="flex justify-between gap-2">
                    <span className="truncate">{b.name}</span>
                    <span className="font-semibold text-pink-700 whitespace-nowrap" title={b.days_until === 0 ? "Today" : `in ${b.days_until} day(s)`}>
                      {b.days_until === 0 ? "Today!" : formatMonthDay(b.dob)}
                    </span>
                  </li>
                ))}
              </ul>}
          </Widget>

          <Widget title={`Internship Ending (${internsEnding.length})`} testid="widget-interns-ending" tone="indigo">
            {internsEnding.length === 0 ? <div className="text-xs text-slate-400">No interns ending in next 30 days</div> :
              <ul className="text-xs space-y-1 max-h-44 overflow-y-auto">
                {internsEnding.map((i) => (
                  <li key={i.id} className="flex justify-between gap-2">
                    <span className="truncate">{i.name}</span>
                    <span className="font-semibold text-indigo-700 whitespace-nowrap" title={`in ${i.days_left} day(s)`}>{formatLongDate(i.end_date)}</span>
                  </li>
                ))}
              </ul>}
          </Widget>

          <Widget title={`Probation Ending (${probationEnding.length})`} testid="widget-probation-ending" tone="amber">
            {probationEnding.length === 0 ? <div className="text-xs text-slate-400">No probation ending in next 30 days</div> :
              <ul className="text-xs space-y-1 max-h-44 overflow-y-auto">
                {probationEnding.map((i) => (
                  <li key={i.id} className="flex justify-between gap-2">
                    <span className="truncate">{i.name}</span>
                    <span className="font-semibold text-amber-700 whitespace-nowrap" title={`in ${i.days_left} day(s)`}>{formatLongDate(i.end_date)}</span>
                  </li>
                ))}
              </ul>}
          </Widget>

          <Widget title={`Expiring Documents (${expiringDocs.length})`} testid="widget-expiring-docs" tone="rose" className="md:col-span-2 lg:col-span-4">
            {expiringDocs.length === 0 ? <div className="text-xs text-slate-400">No documents expiring in next 60 days</div> :
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                {expiringDocs.map((d, i) => (
                  <div key={`${d.employee_id}-${i}`} className="flex justify-between gap-2 bg-rose-50 border border-rose-100 rounded p-2">
                    <span className="truncate"><b>{d.name}</b> · {d.document}</span>
                    <span className="font-semibold text-rose-700 whitespace-nowrap" title={`in ${d.days_left} day(s)`}>{formatLongDate(d.expiry_date)}</span>
                  </div>
                ))}
              </div>}
          </Widget>
        </div>
        <div className="bg-white border border-slate-200 rounded p-6">
          <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
            <Building className="h-5 w-5 text-[#226DB4]" /> Welcome, {user?.name}
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            Manage employees, departments, leaves, and payroll from one place.
          </p>
        </div>

        {charts && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6" data-testid="admin-charts">
            <ChartCard title="Headcount by Department">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={charts.by_department} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="department" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="active" stackId="a" fill="#089949" name="Active" />
                  <Bar dataKey="onboarding" stackId="a" fill="#f39c12" name="Onboarding" />
                  <Bar dataKey="inactive" stackId="a" fill="#dd4b39" name="Inactive" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Employment Type Mix">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={charts.by_employment_type}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={(entry) => `${entry.type}: ${entry.count}`}
                  >
                    {charts.by_employment_type.map((entry, i) => (
                      <Cell key={entry.type} fill={["#226DB4", "#089949", "#f39c12", "#605ca8", "#dd4b39"][i % 5]} />
                    ))}
                  </Pie>
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="New Joiners — Last 6 Months">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={charts.joiners_last_6_months} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="joiners" stroke="#226DB4" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Leave Requests — Last 90 Days">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={charts.leaves_last_90_days} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="status" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="count" fill="#226DB4" radius={[4, 4, 0, 0]}>
                    {charts.leaves_last_90_days.map((entry) => (
                      <Cell
                        key={entry.status}
                        fill={
                          entry.status === "Approved" ? "#089949"
                          : entry.status === "Pending" ? "#f39c12"
                          : entry.status === "Rejected" ? "#dd4b39"
                          : "#94a3b8"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}
      </AdminLayout>
    );
  }

  // Employee dashboard — same layout shell as Admin (stat cards → widgets → welcome → charts)
  const pendingCount = myLeaves.filter((l) => l.status === "Pending").length;
  const approvedCount = myLeaves.filter((l) => l.status === "Approved").length;
  const myPunch = essDash?.my_today;
  const deptBirthdays = essDash?.upcoming_birthdays || [];
  const onLeaveToday = essDash?.on_leave_today || [];
  const punchStatus = myPunch?.status || "Not marked";
  const punchStyle = PUNCH_STATUS_STYLE[punchStatus] || "bg-slate-100 text-slate-700 border-slate-200";
  const canClockIn = !myPunch?.check_in;
  const canClockOut = Boolean(myPunch?.check_in && !myPunch?.check_out);
  const leaveRemainingTotal = visibleBalances.reduce(
    (sum, b) => sum + Math.max(0, (b.allocated || 0) - (b.used || 0)),
    0
  );
  const pendingApprovals = [
    ...(approvalInbox.leaves || []).map((lv) => ({
      id: `leave-${lv.id}`,
      kind: "Leave",
      title: ltMap[lv.leave_type_id]?.name || "Leave request",
      detail: `${formatDisplayDate(lv.start_date)} → ${formatDisplayDate(lv.end_date)}${lv.days != null ? ` · ${lv.days}d` : ""}`,
    })),
    ...(approvalInbox.letters || []).map((lt) => ({
      id: `letter-${lt.id}`,
      kind: "Letter",
      title: lt.title || lt.type || "Letter",
      detail: lt.type || "Pending approval",
    })),
  ];
  const dashPunch = async (kind) => {
    setPunchBusy(true);
    setPunchErr("");
    try {
      if (kind === "in") {
        await api.post("/attendance/self/clock-in", { status: "Present" });
      } else {
        await api.post("/attendance/self/clock-out");
      }
      const d = await api.get("/me/dashboard", { params: { days: 30 } });
      setEssDash(d.data);
    } catch (ex) {
      setPunchErr(ex?.response?.data?.detail || "Punch failed");
    } finally {
      setPunchBusy(false);
    }
  };

  const cards = [
    { label: "Today's Status", value: punchStatus, color: "#226DB4", Icon: Clock, to: "/me/attendance" },
    { label: "Clock In", value: myPunch?.check_in || "—", color: "#089949", Icon: LogIn, to: "/me/attendance" },
    { label: "Clock Out", value: myPunch?.check_out || "—", color: "#3c8dbc", Icon: LogOut, to: "/me/attendance" },
    { label: "Pending Leaves", value: pendingCount, color: "#605ca8", Icon: CalendarDays, to: "/me/leaves" },
    { label: "Approved Leaves", value: approvedCount, color: "#f39c12", Icon: CheckCircle2, to: "/me/leaves" },
    { label: "Leave Days Left", value: leaveRemainingTotal, color: "#00c0ef", Icon: Wallet, to: "/me/leaves" },
  ];

  return (
    <AdminLayout title="Dashboard" breadcrumbs={["Home", "Dashboard"]}>
      <div data-testid="ess-dashboard">
        <div className="flex items-center justify-end mb-3">
          <button
            onClick={reload}
            data-testid="dashboard-refresh-btn"
            title="Refresh"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-200 bg-white rounded hover:bg-slate-50 text-slate-600"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        {punchErr && (
          <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
            {punchErr}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {cards.map((c) => (
            <button
              key={c.label}
              onClick={() => navigate(c.to)}
              className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden flex items-stretch hover:shadow-md transition text-left"
              data-testid={`dash-card-${c.label.replace(/ /g, "-").toLowerCase()}`}
            >
              <div
                className="flex items-center justify-center text-white px-5"
                style={{ backgroundColor: c.color }}
              >
                <c.Icon className="h-6 w-6" />
              </div>
              <div className="px-4 py-3 flex-1 min-w-0">
                <div className="text-2xl font-bold text-slate-800 leading-none truncate">
                  {c.value}
                </div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-medium">
                  {c.label}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Widget title="Today's Attendance" testid="widget-today-attendance" tone="emerald">
            <div className="space-y-2 text-xs">
              <div className="flex justify-between items-center gap-2">
                <span className="text-slate-500">Status</span>
                <span className={`inline-flex px-2 py-0.5 rounded font-semibold border ${punchStyle}`}>
                  {punchStatus}
                </span>
              </div>
              <div className="flex justify-between"><span className="text-slate-500">Clock in</span><span className="font-semibold tabular-nums">{myPunch?.check_in || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Clock out</span><span className="font-semibold tabular-nums">{myPunch?.check_out || "—"}</span></div>
              <div className="flex flex-wrap gap-1.5 pt-1 border-t border-emerald-100">
                {canClockIn && (
                  <button
                    type="button"
                    disabled={punchBusy}
                    onClick={() => dashPunch("in")}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#089949] text-white font-medium disabled:opacity-60"
                  >
                    <LogIn className="h-3 w-3" /> Clock in
                  </button>
                )}
                {canClockOut && (
                  <button
                    type="button"
                    disabled={punchBusy}
                    onClick={() => dashPunch("out")}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#226DB4] text-white font-medium disabled:opacity-60"
                  >
                    <LogOut className="h-3 w-3" /> Clock out
                  </button>
                )}
                {!canClockIn && !canClockOut && (
                  <span className="text-slate-400">Punch complete</span>
                )}
              </div>
            </div>
          </Widget>

          <Widget title={`Upcoming Birthdays (${deptBirthdays.length})`} testid="ess-dept-birthdays" tone="pink">
            {deptBirthdays.length === 0 ? (
              <div className="text-xs text-slate-400">No birthdays in next 30 days</div>
            ) : (
              <ul className="text-xs space-y-1 max-h-44 overflow-y-auto">
                {deptBirthdays.map((b) => (
                  <li key={b.id} className="flex justify-between gap-2">
                    <span className="truncate">{b.name}{b.is_self ? " (you)" : ""}</span>
                    <span className="font-semibold text-pink-700 whitespace-nowrap">
                      {b.days_until === 0 ? "Today!" : formatMonthDay(b.dob)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Widget>

          <Widget title={`On Leave Today (${onLeaveToday.length})`} testid="ess-dept-on-leave" tone="indigo">
            {onLeaveToday.length === 0 ? (
              <div className="text-xs text-slate-400">No one from your team on leave today</div>
            ) : (
              <ul className="text-xs space-y-1 max-h-44 overflow-y-auto">
                {onLeaveToday.map((p) => (
                  <li key={`${p.id}-${p.start_date}`} className="flex justify-between gap-2">
                    <span className="truncate">{p.name}</span>
                    <span className="font-semibold text-indigo-700 whitespace-nowrap">
                      {formatLongDate(p.start_date)} – {formatLongDate(p.end_date)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Widget>
        </div>

        <div className="bg-white border border-slate-200 rounded p-6">
          <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
            <Building className="h-5 w-5 text-[#226DB4]" /> Welcome, {user?.name}
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            View your attendance, apply leave, check payslips, and manage self-service requests from one place.
          </p>
          <div className="flex flex-wrap gap-2">
            {canClockIn && (
              <button
                type="button"
                disabled={punchBusy}
                onClick={() => dashPunch("in")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-[#089949] hover:bg-[#067A3A] text-white disabled:opacity-60"
              >
                <LogIn className="h-3.5 w-3.5" /> Clock in
              </button>
            )}
            {canClockOut && (
              <button
                type="button"
                disabled={punchBusy}
                onClick={() => dashPunch("out")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-[#226DB4] hover:bg-[#1B5A94] text-white disabled:opacity-60"
              >
                <LogOut className="h-3.5 w-3.5" /> Clock out
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate("/me/leaves")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded hover:bg-slate-50 text-slate-700"
            >
              <CalendarDays className="h-3.5 w-3.5" /> Apply leave
            </button>
            <button
              type="button"
              onClick={() => navigate("/me/attendance")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded hover:bg-slate-50 text-slate-700"
            >
              <Clock className="h-3.5 w-3.5" /> My attendance
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6" data-testid="ess-charts">
          <ChartCard title="Upcoming Holidays">
            <div className="min-h-[220px]">
              {upcomingHolidays.length === 0 ? (
                <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">
                  No upcoming holidays
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-[240px] overflow-y-auto" data-testid="ess-upcoming-holidays">
                  {upcomingHolidays.map((h) => (
                    <li key={h.id} className="py-2 flex justify-between gap-3 text-sm">
                      <span className="truncate font-medium text-slate-800">{h.name}</span>
                      <span className="shrink-0 text-slate-600 tabular-nums whitespace-nowrap">
                        {formatLongDate(h.date)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => navigate("/holidays")}
                className="mt-2 text-xs font-medium text-[#226DB4] hover:underline"
              >
                Open holiday calendar →
              </button>
            </div>
          </ChartCard>

          <ChartCard title={`My Approvals — Pending (${pendingApprovals.length})`}>
            <div className="min-h-[220px]">
              {pendingApprovals.length === 0 ? (
                <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">
                  Nothing pending
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-[240px] overflow-y-auto" data-testid="ess-pending-approvals">
                  {pendingApprovals.slice(0, 10).map((item) => (
                    <li key={item.id} className="py-2 flex justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">{item.title}</div>
                        <div className="text-xs text-slate-500 truncate">{item.detail}</div>
                      </div>
                      <span className="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                        {item.kind}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => navigate("/approvals")}
                className="mt-2 text-xs font-medium text-[#226DB4] hover:underline"
              >
                Open my approvals →
              </button>
            </div>
          </ChartCard>
        </div>
      </div>
    </AdminLayout>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded shadow-sm">
      <div className="px-5 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}



/* Phase 10 widget shell */
function Widget({ title, children, tone = "blue", testid, className = "" }) {
  const tones = {
    emerald: "border-emerald-200 bg-white",
    pink:    "border-pink-200 bg-white",
    indigo:  "border-indigo-200 bg-white",
    amber:   "border-amber-200 bg-white",
    rose:    "border-rose-200 bg-white",
    blue:    "border-blue-200 bg-white",
  };
  const headerTones = {
    emerald: "text-emerald-700",
    pink:    "text-pink-700",
    indigo:  "text-indigo-700",
    amber:   "text-amber-700",
    rose:    "text-rose-700",
    blue:    "text-blue-700",
  };
  return (
    <div className={`border rounded p-3 shadow-sm ${tones[tone]} ${className}`} data-testid={testid}>
      <div className={`text-xs uppercase tracking-wider font-semibold mb-2 ${headerTones[tone]}`}>{title}</div>
      {children}
    </div>
  );
}
